import { useEffect, useState } from 'react';
import { alertHistory, oobPending, oobVerify, type OobPending, type SentAlert } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { sx } from '../../ui/designStyles';

// Restored from the Flutter Incidents tab (incidents_view.dart:268, :400): out-of-band
// confirmations waiting for their 6-digit code, and the outbound alert history. Drawn in
// the design's own Incidents markup (OOB cards T4876-4907, dispatch history T4759-4798),
// each only while it has something to show. Governance-tenant data: Aithora only.

const DAY_MS = 24 * 3_600_000;
const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const ago = (iso: string, now: number) => {
  const m = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
};
const SEV: Record<number, [string, string]> = { 0: ['INFO', '#54606C'], 1: ['MEDIUM', '#5B6B7C'], 2: ['HIGH', '#B3261E'], 3: ['KILL', '#8C3A33'] };
const CAPTION = 'font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#54606C;';

export function OobConfirmations() {
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const q = useQuery((signal) => oobPending(client, { signal }).catch(() => null), [client], { pollMs: 15_000 });
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [result, setResult] = useState<Record<string, 'ok' | 'bad'>>({});
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const rows = q.data?.pending ?? [];
  if (!rows.length) return null;

  const verify = async (o: OobPending) => {
    const otp = codes[o.id] ?? '';
    if (otp.length !== 6 || roReason) return;
    try {
      const r = await oobVerify(client, { body: { requestId: o.id, otp } });
      setResult((x) => ({ ...x, [o.id]: r.valid ? 'ok' : 'bad' }));
      if (r.valid) q.reload();
    } catch {
      setResult((x) => ({ ...x, [o.id]: 'bad' }));
    }
  };

  return (
    <section aria-label="Out-of-band confirmations" data-testid="oversight-oob" style={sx('flex:0 0 auto;min-width:0;display:flex;flex-direction:column;gap:12px;border-top:1px solid rgba(34,39,46,.14);padding-top:8px;')}>
      <div style={sx(CAPTION)}>{`Out-of-band confirmations · ${rows.length}`}</div>
      {rows.map((o) => {
        const val = codes[o.id] ?? '';
        const full = val.length === 6 && !roReason;
        const res = result[o.id];
        return (
          <div key={o.id} data-testid={`oob-${o.id}`} style={sx('border:1px solid rgba(34,39,46,.13);background:#FCFAF4;border-radius:12px;padding:14px 16px;min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
              <span style={sx('font-size:16px;font-weight:700;')}>{o.actionSummary || o.actionId}</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#5B6B7C;')}>{`expires ${mmss(o.expiresAt - now)}`}</span>
            </div>
            <div style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;margin-top:7px;')}>{`${String(o.channel).toUpperCase()} → the registered contact`}</div>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-top:10px;')}>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                aria-label={`Code for ${o.actionSummary || o.actionId}`}
                value={val}
                onChange={(e) => {
                  setCodes((c) => ({ ...c, [o.id]: String(e.target.value || '').replace(/\D/g, '').slice(0, 6) }));
                  setResult((x) => { const n = { ...x }; delete n[o.id]; return n; });
                }}
                placeholder="000000"
                style={sx('width:132px;height:36px;padding:0 14px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#FFFDF8;font-variant-numeric:tabular-nums;font-size:16px;letter-spacing:.24em;color:#22272E;outline:none;')}
              />
              <a
                href="#"
                aria-disabled={!full || undefined}
                title={roReason ?? undefined}
                onClick={(e) => { e.preventDefault(); void verify(o); }}
                style={sx('display:inline-flex;align-items:center;justify-content:center;height:36px;padding:0 16px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;'
                  + (full ? 'background:#2F5C97;color:#fff;' : 'background:rgba(34,39,46,.07);color:#54606C;pointer-events:none;'))}
              >
                Verify
              </a>
              <span role={res === 'bad' ? 'alert' : undefined} style={sx(`font-size:14px;color:${res === 'bad' ? '#B3261E' : '#54606C'};`)}>
                {res === 'bad' ? 'Code rejected. The action was not run.' : full ? 'Ready to verify.' : 'Enter the 6-digit code sent to this number.'}
              </span>
            </div>
          </div>
        );
      })}
    </section>
  );
}

/** Group key: the incident or threat an alert is about, else its title. */
function groupOf(a: SentAlert): string {
  const d = a.data ?? {};
  const k = d.incidentId ?? d.threatId ?? d.playbookId ?? d.sessionId;
  return typeof k === 'string' && k ? k : a.title;
}

export function AlertHistory() {
  const { client } = useSession();
  const q = useQuery((signal) => alertHistory(client, { signal }).catch(() => null), [client], { pollMs: 60_000 });
  const now = Date.now();
  const rows = (q.data?.notifications ?? []).filter((a) => now - Date.parse(a.sentAt) <= DAY_MS);
  if (!rows.length) return null;
  const groups = new Map<string, SentAlert[]>();
  for (const a of rows) groups.set(groupOf(a), [...(groups.get(groupOf(a)) ?? []), a]);
  return (
    <section aria-label="Dispatch history" data-testid="oversight-alerts" style={sx('flex:0 0 auto;min-width:0;border-top:1px solid rgba(34,39,46,.14);')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;padding:11px 15px 11px 0;border-bottom:1px solid rgba(34,39,46,.09);')}>
        <span style={sx('font-size:16px;font-weight:700;')}>Dispatch history · 24h</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:14px;color:#54606C;')}>Grouped by incident. Delivery is recorded, not editable.</span>
      </div>
      {[...groups.entries()].map(([key, list]) => {
        const worst = Math.max(...list.map((a) => a.severity));
        return (
          <div key={key} style={sx('border-top:1px solid rgba(34,39,46,.09);min-width:0;')}>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;padding:9px 15px;background:rgba(34,39,46,.025);min-width:0;')}>
              <span style={sx(`width:6px;height:6px;border-radius:50%;flex:0 0 6px;background:${(SEV[worst] ?? SEV[0])[1]};`)} />
              <span style={sx('font-size:14px;font-weight:700;min-width:0;overflow-wrap:break-word;')}>{list[0].title}</span>
              <span style={sx('flex:1;')} />
              <span style={sx('font-size:14px;font-weight:700;color:#54606C;white-space:nowrap;')}>{list.length + (list.length === 1 ? ' alert' : ' alerts')}</span>
            </div>
            {list.map((a) => {
              const [w, c] = SEV[a.severity] ?? SEV[0];
              return (
                <div key={a.id} style={sx('display:grid;grid-template-columns:64px minmax(0,1fr) 92px 82px 62px;gap:12px;align-items:center;padding:10px 15px 10px 30px;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                  <span style={sx(`font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;color:${c};`)}>{w}</span>
                  <span style={sx('min-width:0;')}>
                    <span style={sx('display:block;font-size:16px;font-weight:600;overflow-wrap:break-word;')}>{a.title}</span>
                    <span style={sx('display:block;font-size:14px;color:#54606C;overflow-wrap:break-word;')} title={a.error}>{a.body}</span>
                  </span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;')}>{a.channel}</span>
                  <span style={sx('display:inline-flex;align-items:center;gap:5px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.06em;border-radius:20px;padding:2px 8px;white-space:nowrap;'
                    + (a.delivered ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#B3261E;background:rgba(179,38,30,.14);'))}>
                    {a.delivered ? 'delivered' : 'failed'}
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;text-align:right;white-space:nowrap;')}>{ago(a.sentAt, now)}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </section>
  );
}
